import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { ReactNode } from "react";

import Button, { EButtonstyletype, EButtonVariant, IButtonProps } from "../Button";
import IconButton, { EIconButtonVariant } from "../IconButton";
import Typography, { ETypo } from "../Typography";
import classes from "./classes.module.scss";

const BUTTON_DEFAULTS = [
	{ variant: EButtonVariant.PRIMARY, styletype: EButtonstyletype.OUTLINED },
	{ variant: EButtonVariant.PRIMARY, styletype: EButtonstyletype.CONTAINED },
	{ variant: EButtonVariant.SECONDARY, styletype: EButtonstyletype.TEXT },
] as const;

function ModalFooterButton({
	button,
	defaultVariant,
	defaultStyletype,
}: {
	button: IButtonProps | undefined;
	defaultVariant: EButtonVariant;
	defaultStyletype: EButtonstyletype;
}): React.ReactElement | null {
	if (!button) return null;
	return (
		<Button
			{...button}
			variant={button.variant ?? defaultVariant}
			styletype={button.styletype ?? defaultStyletype}
			fullwidth={button.fullwidth ?? true}>
			{button.children}
		</Button>
	);
}

export function ModalBackdrop(props: { onClose?: () => void; closeLabel: string }): React.ReactElement {
	const { onClose, closeLabel } = props;
	if (onClose) {
		return (
			<button
				type="button"
				className={classes["backdrop"]}
				onClick={onClose}
				aria-label={closeLabel}
				data-testid="confirm-modal-backdrop"
			/>
		);
	}
	return <div className={classes["backdrop"]} aria-hidden data-testid="confirm-modal-backdrop" />;
}

export type ModalInnerProps = {
	contentClassNames: string;
	className?: string;
	title?: string;
	children?: ReactNode;
	firstButton?: IButtonProps;
	secondButton?: IButtonProps;
	thirdButton?: IButtonProps;
	onClose?: () => void;
	closeLabel: string;
};

export function ModalInner(props: ModalInnerProps): React.ReactElement {
	const { contentClassNames, className, title, children, firstButton, secondButton, thirdButton, onClose, closeLabel } =
		props;
	const buttons = [firstButton, secondButton, thirdButton] as const;
	return (
		<div className={contentClassNames}>
			<div className={classes["header"]}>
				{title && <Typography typo={ETypo.TITLE_H4}> {title}</Typography>}
				{onClose ? (
					<IconButton variant={EIconButtonVariant.NEUTRAL} onClick={onClose} icon={<XMarkIcon />} ariaLabel={closeLabel} />
				) : null}
			</div>
			<div className={classNames(classes["children"], className)}>{children}</div>
			<div className={classes["footer"]}>
				{buttons.map((btn, i) => {
					const def = BUTTON_DEFAULTS[i];
					return def ? (
						<ModalFooterButton
							key={i}
							button={btn}
							defaultVariant={def.variant}
							defaultStyletype={def.styletype}
						/>
					) : null;
				})}
			</div>
		</div>
	);
}
